import React, { useEffect, useState, type ReactNode } from 'react';
import { ChevronDown, Pencil } from 'lucide-react';
import { useResourceKit } from '@resource-kit/core/react';
import type { CellContext, ColumnDef } from '@tanstack/react-table';
import { PageShell } from '../primitives/PageShell';
import { Button } from '../primitives/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../primitives/Card';
import { LoadingPage } from '../primitives/Loading';
import { PageHeader } from '../primitives/PageHeader';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../primitives/Tabs';
import { cn } from '../../cn';
import { failLoud } from '@resource-kit/core';
import type { ResourceBundle } from '@resource-kit/controllers/react';
import type { EntityField } from '@resource-kit/controllers/react';
import {
  formNodesOf,
  type AnyFieldDef,
  type FormLeaf,
  type FormNode,
  type GroupDisplay,
} from '@resource-kit/model';
import { FieldSlot } from './FieldSlot';
import { useFormView, type FormViewController } from '@resource-kit/controllers/react';
import { useFormViewSlots } from './slots';
import type { ViewFieldOverride } from '@resource-kit/controllers';

/**
 * `FormView` — the single view for one record (#1797).
 *
 * It replaces the pair `DataDetailView` (readonly) + `ResourceFormDialog`
 * (a Modal stacked on top of the Sheet inside the Drawer, the P0 this issue
 * removes). Editing is now an IN-PLACE mode switch on the same tree: the field
 * slots, their groups and their collapse state are never unmounted, so scroll
 * position and expansion survive an edit.
 *
 * Save is record-level and explicit. Per-field autosave was rejected: it makes
 * cross-field validation report errors caused by unchanged fields, it has no
 * atomic rollback (so "discard" loses its meaning), and it turns the conflict
 * surface into a field-level cartesian product.
 */
export interface FormViewProps<T, F extends Record<string, AnyFieldDef>> {
  bundle: ResourceBundle<T, F>;
  /** Detail columns; supply the read-only projection of each field. */
  columns: ColumnDef<T>[];
  /** Server snapshot. `null` + `intent="create"` starts an unsaved record. */
  record: T | null;
  recordId?: string;
  initialValues?: Partial<T>;
  intent?: 'record' | 'create';
  /** Operator may edit (permission + an `update` API). */
  canEdit?: boolean;
  /**
   * Operator may read this record's change history — the admin role OR the
   * `audit:read` grant since #1964, resolved by the host (#1804). Injected
   * like `canEdit` rather than read from an auth hook here:
   * the identity fact belongs to the host, and the kit only composes it with
   * the resource's `auditResourceType` coverage. Absent ⇒ no history, because
   * a missing wiring must never read as "allowed".
   */
  canViewAudit?: boolean;
  /**
   * The verdict for every `custom` node that declared `gated: true`, keyed by
   * node id (#1917). Injected in the same direction as `canViewAudit`: the
   * host owns the identity fact, the kit only composes it. A gated node with
   * no entry here — or with `tabGates` absent entirely — is HIDDEN, because a
   * missing wiring must never read as "allowed".
   */
  tabGates?: Readonly<Record<string, boolean>>;
  /** Validation layer 3: per-item overrides this view tightens, by field name. */
  viewOverrides?: Readonly<Record<string, ViewFieldOverride>>;
  /** Compact drawer layout vs. the full-page card grid. */
  embedded?: boolean;
  /** Show the saved record identity when the embedding surface owns a header. */
  showEmbeddedHeader?: boolean;
  loading?: boolean;
  error?: string;
  title?: string;
  subtitle?: ReactNode;
  titleAdornment?: ReactNode;
  /** Extra header actions rendered beside the built-in Edit button. */
  actions?: ReactNode;
  onSaved?: (saved: T) => void;
  onCloseCreate?: () => void;
  /**
   * Lets a CONTAINER (a Modal backdrop, a route blocker) re-assert the same
   * discard guard on close affordances FormView does not own.
   */
  onDirtyChange?: (dirty: boolean) => void;
}

/**
 * The id to ask the change-history ledger for (#1963) — NOT necessarily the id
 * the record is addressed by.
 *
 * `recordId` is `idField`'s value: it goes into the detail URL and into
 * `api.update`. The ledger keys its rows on the internal UUID primary key
 * instead (`contexts/commerce/store/*.ts` — `before?.id ?? newId()`). For
 * `orders` / `dispatch/jobs` those are the same column and the default holds;
 * `products` / `customers` are addressed by `shopify_id`, so before they named
 * a ledger field their history queried a key space that had never been written
 * — and an empty ledger response is indistinguishable from an untouched record.
 *
 * A named field the record does not carry returns null (⇒ no tab) rather than
 * falling back to `recordId`: that fallback is precisely the failure this
 * exists to stop, and it fails SILENTLY. Deliberately narrow about what counts
 * as carried — a non-string or an empty string is a miss, because a `''`
 * resource id would query the whole ledger's worth of nothing just as quietly.
 */
function ledgerIdOf(
  model: { name: string; auditResourceIdField?: string },
  record: unknown,
  recordId: string | undefined,
): string | null {
  const field = model.auditResourceIdField;
  if (field == null) return recordId ?? null;
  const value = (record as Record<string, unknown> | null | undefined)?.[field];
  if (typeof value === 'string' && value !== '') return value;
  failLoud(
    `FormView: model "${model.name}" names "${field}" as its change-history id, ` +
      `but the record does not carry a usable value there.`,
  );
  return null;
}

/**
 * The inner containers a `fields` surface draws its slots in — the full-page
 * two-column grid and the embedded stack. Named once because THREE surfaces
 * share them: a `fields` node (on the rail or inside a `FormGroup`), the
 * "Other" card, and the ungrouped "Details" fallback. A rail panel that is
 * meant to look like a card body has to BE the same string, not a copy of it
 * that can drift.
 */
const FIELD_GRID_CLASS = 'grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6';
const FIELD_STACK_CLASS = 'divide-y divide-border/50';

/** The closed `GroupDisplay` set, checked on every group (root and nested) because a cast can bypass the type. */
const GROUP_DISPLAYS: readonly GroupDisplay[] = ['card', 'tab', 'inline'];

// ─── column helpers (moved verbatim from DataDetailView) ───

const getAccessorKey = <T,>(col: ColumnDef<T>): string | undefined =>
  'accessorKey' in col && col.accessorKey != null ? String(col.accessorKey) : undefined;

const columnKey = <T,>(col: ColumnDef<T>): string => String(col.id ?? getAccessorKey(col) ?? '');

/** Raw json/array without a custom renderer produces block-level content. */
const isBlockRenderer = <T,>(col: ColumnDef<T> | undefined): boolean => {
  const field = col?.meta?.field;
  if (!field) return false;
  const effective = field.renderType || field.type;
  return (effective === 'json' || effective === 'array') && !field.render;
};

/**
 * One collapsible / plain group section. Its open state lives HERE, which is
 * precisely why a mode switch cannot reset it — `FormView` never remounts it.
 *
 * `chrome` is the frame: `'card'` a full-page `Card`, `'heading'` a titled
 * section (the drawer, and `'inline'` groups everywhere). The body is the
 * caller's: a `fields` node brings its own grid / stack container.
 *
 * A folded group does not mount its children (#2783): a self-fetching leaf
 * inside it first requests its data when the operator expands it.
 */
function FormGroup({
  title,
  chrome,
  collapsible = false,
  defaultCollapsed = false,
  children,
}: {
  title?: string;
  chrome: 'card' | 'heading';
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(!defaultCollapsed);
  const expanded = !collapsible || open;
  const chevron = (
    <ChevronDown
      className={cn(
        'w-4 h-4 text-muted-foreground transition-transform shrink-0',
        open && 'rotate-180',
      )}
    />
  );

  if (chrome === 'heading') {
    return (
      <section>
        {collapsible ? (
          <button
            type="button"
            className="flex w-full items-center justify-between gap-2 cursor-pointer select-none"
            aria-expanded={open}
            onClick={() => setOpen(!open)}
          >
            <h3 className="text-sm font-semibold text-foreground">{title}</h3>
            {chevron}
          </button>
        ) : title !== undefined ? (
          <h3 className="text-sm font-semibold text-foreground mb-1">{title}</h3>
        ) : null}
        {expanded && children}
      </section>
    );
  }

  if (title === undefined) {
    // No header, so the content restores the top padding `CardContent`
    // drops for the header it normally follows.
    return (
      <Card>
        <CardContent className="pt-6">{children}</CardContent>
      </Card>
    );
  }

  return (
    <Card>
      {collapsible ? (
        <button
          type="button"
          className="flex w-full items-center justify-between gap-2 p-6 cursor-pointer select-none"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          <CardTitle>{title}</CardTitle>
          {chevron}
        </button>
      ) : (
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
      )}
      {expanded && <CardContent>{children}</CardContent>}
    </Card>
  );
}

/** The record-level save bar. Present whenever the record is not readonly. */
function SaveBar<T>({
  controller,
  isCreate,
}: {
  controller: FormViewController<T>;
  isCreate: boolean;
}) {
  const { t } = useResourceKit();
  const saving = controller.mode === 'saving';
  return (
    <div
      role="status"
      aria-busy={saving}
      className="flex items-center justify-end gap-2 border-t border-border pt-3"
    >
      {controller.formError && (
        <p className="text-sm text-destructive mr-auto">{controller.formError}</p>
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={controller.cancel}
        disabled={saving}
      >
        {t('rk.ui.cancel')}
      </Button>
      <Button
        type="button"
        size="sm"
        onClick={controller.save}
        disabled={saving || (!isCreate && !controller.isDirty)}
      >
        {saving
          ? isCreate
            ? t('rk.resource.creating')
            : t('rk.ui.saving')
          : isCreate
            ? t('rk.ui.create')
            : t('rk.ui.save')}
      </Button>
    </div>
  );
}

export function FormView<T, F extends Record<string, AnyFieldDef>>({
  bundle,
  columns,
  record,
  recordId,
  initialValues,
  intent = 'record',
  canEdit = false,
  canViewAudit = false,
  tabGates,
  viewOverrides,
  embedded = false,
  loading,
  error,
  title,
  subtitle,
  titleAdornment,
  actions,
  showEmbeddedHeader = false,
  onSaved,
  onCloseCreate,
  onDirtyChange,
}: FormViewProps<T, F>) {
  const { form, host, model } = bundle;
  const { t, i18n } = useResourceKit();
  const slots = useFormViewSlots();
  const isCreate = intent === 'create';
  /** Null until the operator picks a tab; the rail then falls back to the first declared one. */
  const [activeTab, setActiveTab] = useState<string | null>(null);

  const controller = useFormView({
    bundle,
    record,
    recordId,
    initialValues,
    intent,
    canEdit,
    viewOverrides,
    onSaved,
    onCloseCreate,
    // The host half of the error contract, now a SLOT (#2169 PR-3). It used to
    // be composed right here, and could not stay: both calls are host facts —
    // reading `ApiError`, this app's HTTP client's error class, and resolving a
    // key against this app's catalogs — that no package can make.
    //
    // What this file still owns is the CONTEXT, because only a component can
    // read the seam: `t` translates, `i18n.exists` answers the one
    // non-translation question, and the namespace is this resource's slug. The
    // probe stays separate from the translator on purpose — a probe that also
    // translated would be a second `t`.
    describeError: (error) =>
      slots.describeError<T>(error, {
        t,
        exists: (key) => i18n.exists(key),
        namespace: host.slug,
      }),
  });

  const editing = controller.mode !== 'readonly';
  const { isDirty } = controller;
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const draft = (controller.node.draft ?? {}) as Record<string, unknown>;
  const data = (record ?? {}) as Record<string, unknown>;

  // Esc while dirty routes through the discard confirm and must NOT reach the
  // Drawer (design §6.3) — otherwise the container closes and the input is gone.
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'Escape' || !controller.isDirty) return;
    event.preventDefault();
    event.stopPropagation();
    controller.cancel();
  };

  const fieldFor = (key: string): EntityField<T> | undefined =>
    controller.fields.find((f) => String(f.key) === key);

  const renderDisplay = (col: ColumnDef<T>): ReactNode => {
    const accessor = getAccessorKey(col);
    const getValue = () => (accessor != null ? data[accessor] : undefined);
    const ctx = {
      row: { original: record as T, getValue: (key: string) => data[key] },
      getValue,
      column: { columnDef: col },
      // Partial mock — only the slice renderers read, not the full table
      // runtime. Deliberate double assertion, not `any`.
    } as unknown as CellContext<T, unknown>;
    return typeof col.cell === 'function' ? col.cell(ctx) : (getValue() as ReactNode);
  };

  const columnLabel = (col: ColumnDef<T>): string => {
    const meta = col.meta;
    const raw =
      meta?.filter?.label ||
      meta?.label ||
      (typeof col.header === 'string' ? col.header : null) ||
      col.id ||
      getAccessorKey(col) ||
      'Field';
    return t(raw, { defaultValue: raw });
  };

  const renderSlot = (col: ColumnDef<T>, index: number): ReactNode => {
    const key = columnKey(col) || String(index);
    const field = fieldFor(key);
    const layout = embedded ? 'compact' : 'card';

    // A column with no form field (a synthetic / hidden-from-form column)
    // has nothing to edit — render its value and stop.
    if (!field) {
      return (
        <div
          key={key}
          className={cn(
            'min-w-0',
            layout === 'card' ? 'space-y-1.5' : 'flex justify-between gap-4 py-2',
          )}
        >
          <span className="text-sm text-muted-foreground shrink-0">{columnLabel(col)}</span>
          <div className="text-sm text-foreground break-words overflow-hidden min-w-0">
            {renderDisplay(col)}
          </div>
        </div>
      );
    }

    return (
      <FieldSlot
        key={key}
        field={field}
        display={renderDisplay(col)}
        value={draft[key]}
        mode={editing ? 'edit' : 'readonly'}
        editable={controller.isEditable(field)}
        busy={controller.mode === 'saving'}
        issues={controller.issuesFor(key)}
        layout={layout}
        block={isBlockRenderer(col)}
        onChange={(next) => controller.setValue(key, next)}
        onBlur={() => controller.blurField(key)}
        onRequestEdit={() => controller.enterEdit(key)}
        focusToken={controller.focusTokenFor(key)}
      />
    );
  };

  /**
   * A create form has no columns to project from — it is a flat grid of the
   * model's form fields.
   */
  const renderCreateFields = (): ReactNode => (
    <div
      className={
        host.formConfig?.columns === 1 ? 'grid grid-cols-1 gap-4' : 'grid grid-cols-2 gap-3'
      }
    >
      {controller.fields.map((field) => {
        const key = String(field.key);
        const fullWidth = field.type === 'json' || field.type === 'array';
        return (
          <div
            key={key}
            className={cn(host.formConfig?.columns !== 1 && fullWidth && 'col-span-2')}
          >
            <FieldSlot
              field={field}
              display={null}
              value={draft[key]}
              mode="edit"
              // NOT a bare `editable` (#1871). A create form is
              // entirely in edit, but "in edit" is not the same
              // question as "may this field be written": a column
              // the server assigns must stay frozen here too, and
              // that is exactly what a hardcoded `true` erased.
              editable={controller.isEditable(field)}
              busy={controller.mode === 'saving'}
              issues={controller.issuesFor(key)}
              layout="card"
              onChange={(next) => controller.setValue(key, next)}
              onBlur={() => controller.blurField(key)}
              onRequestEdit={() => {}}
              focusToken={controller.focusTokenFor(key)}
            />
          </div>
        );
      })}
    </div>
  );

  // ─── the form tree (#2783) and its rail (#1804, design §6.7) ───
  //
  // Create intentionally drops the whole tree; the flat field grid remains.
  const allNodes = formNodesOf(form);
  const fieldLeaves = allNodes.filter(
    (node): node is Extract<FormLeaf<T, F>, { kind: 'fields' }> => node.kind === 'fields',
  );
  const rootChildren = isCreate ? [] : form.children;

  /** Declares that a gate exists on this node (#1917); the verdict arrives in `tabGates`. */
  const declaresGate = (node: FormNode<T, F>): boolean => 'gated' in node && node.gated === true;

  /**
   * Authoring checks read `form`, not the `rootChildren` the create strip
   * empties: a guardrail that skips create is not a guardrail. Every node of
   * the tree — group or leaf, at any depth — shares ONE id namespace.
   *
   * Every group's `groupDisplay` — the root's and each nested one's — is
   * checked against the closed set at runtime by the same function, because
   * a cast can bypass the type and the renderers read it. Only the root may
   * use `'tab'`.
   */
  const checkGroupDisplay = (where: string, display: unknown, allowTab: boolean): void => {
    if (!(GROUP_DISPLAYS as readonly unknown[]).includes(display)) {
      failLoud(
        display === undefined
          ? `${where} has no groupDisplay.`
          : `${where} has invalid groupDisplay "${String(display)}".`,
      );
    }
    if (!allowTab && display === 'tab') {
      failLoud(`${where} uses groupDisplay "tab", which only the form root may use.`);
    }
  };
  checkGroupDisplay('FormView form root', form.groupDisplay, true);
  const declaredNodeIds = new Set<string>();
  const gatedTabIds = new Set<string>();
  for (const node of allNodes) {
    if (declaredNodeIds.has(node.id)) {
      failLoud(`FormView declares the node id "${node.id}" twice.`);
    }
    declaredNodeIds.add(node.id);
    if (declaresGate(node)) gatedTabIds.add(node.id);
    if (node.collapsible === true && node.label === undefined) {
      failLoud(`FormView node "${node.id}" is collapsible but has no label.`);
    }
    if (node.kind !== 'group') continue;
    checkGroupDisplay(`FormView group "${node.id}"`, node.groupDisplay, false);
    if (node.children.length === 0) failLoud(`FormView group "${node.id}" has no children.`);
  }
  if (form.groupDisplay === 'tab') {
    for (const child of form.children) {
      if (child.label === undefined) failLoud(`FormView tab "${child.id}" has no label.`);
    }
  }
  /**
   * The change history is DECLARED, not derived (#2783): a covered model
   * places exactly one `audit` node where it belongs in the tree, and an
   * uncovered one may not declare one. Deriving it had no fixed place to land once the
   * root could be a `'card'` / `'inline'` group, or once the node could sit
   * inside a group beside other content.
   */
  const auditNodeCount = allNodes.filter((node) => node.kind === 'audit').length;
  if (auditNodeCount > 1) {
    failLoud(
      `FormView: model "${model.name}" declares ${auditNodeCount} audit nodes; at most one.`,
    );
  }
  const declaresAudit = auditNodeCount > 0;
  if (declaresAudit !== (model.auditResourceType != null)) {
    failLoud(
      declaresAudit
        ? `FormView: model "${model.name}" declares an audit node but no auditResourceType.`
        : `FormView: model "${model.name}" declares auditResourceType but no audit node.`,
    );
  }
  /**
   * One branch, three shapes: a key naming no node, one naming a node that
   * declares no gate, and — the shape that LEAKS — one whose node forgot
   * `gated`, which renders that node to everyone. A key merely absent from
   * `tabGates` is a verdict, not a mistake.
   */
  for (const key of Object.keys(tabGates ?? {})) {
    if (!gatedTabIds.has(key)) {
      failLoud(
        `FormView received a tabGates key "${key}", which names no tab that declares a gate.`,
      );
    }
  }

  /**
   * The change-history gate, carried over from the collapsible section this
   * replaced (`ResourceDetailPanel`, #1804 batch B): the operator may read
   * the ledger AND the backend's closed `resource_type` enum covers this
   * resource AND there is a saved record to have a history of. Resolved to a
   * NON-NULL target so the audit node needs no assertion.
   *
   * The third condition is the one #1963 sharpened. It used to read "a
   * `recordId` was passed"; it now reads "the LEDGER's id resolves" — see
   * `ledgerIdOf`, which is where the two key spaces are told apart. The
   * conditions themselves are otherwise unchanged, and the second `!= null`
   * below is narrowing, not a fourth condition: TS does not carry the check
   * on `model.auditResourceType` across the statement boundary.
   */
  const ledgerId =
    !isCreate && canViewAudit && model.auditResourceType != null
      ? ledgerIdOf(model, record, recordId)
      : null;
  const auditTarget =
    ledgerId != null && model.auditResourceType != null
      ? { resourceType: model.auditResourceType, resourceId: ledgerId }
      : null;

  /**
   * The runtime gate over the tree. A `custom` node that declared `gated` is
   * kept only on an explicit `true`, so both an unwired `tabGates` and a
   * missing entry hide it; an `audit` node is kept only with a resolved
   * `auditTarget`; a group is dropped once the gate has removed ALL of its
   * children.
   *
   * The gate never removes a `fields` leaf, and that is a TYPE fact:
   * `gated?: true` exists solely on the `custom` leaf of `FormLeaf`, and
   * `audit` answers only to `auditTarget`. So a group holding a `fields` leaf
   * can never be emptied, and `renderOther` may build its "already grouped"
   * set from the UNGATED tree. If #1917 ever widens `gated` to `fields` or
   * `group` nodes this stops holding — a gated-away leaf would leave the tree
   * AND stay excluded from the Other card, so its fields would render
   * nowhere. Any such widening must be paired with a fallback for the
   * dropped fields.
   */
  const gate = (node: FormNode<T, F>): FormNode<T, F> | null => {
    if (node.kind === 'custom') {
      return !declaresGate(node) || tabGates?.[node.id] === true ? node : null;
    }
    if (node.kind === 'audit') return auditTarget != null ? node : null;
    if (node.kind === 'fields') return node;
    const kept = node.children.map(gate).filter((child): child is FormNode<T, F> => child !== null);
    return kept.length === 0 ? null : { ...node, children: kept };
  };
  const visibleChildren = rootChildren
    .map(gate)
    .filter((child): child is FormNode<T, F> => child !== null);
  const railTabs = form.groupDisplay === 'tab' ? visibleChildren : [];
  const hasRail = railTabs.length > 0;

  /** The `fields` leaves a rail tab holds, at any depth below it. */
  const fieldLeavesOf = (tab: FormNode<T, F>): Extract<FormLeaf<T, F>, { kind: 'fields' }>[] =>
    (tab.kind === 'group' ? formNodesOf(tab) : [tab]).filter(
      (node): node is Extract<FormLeaf<T, F>, { kind: 'fields' }> => node.kind === 'fields',
    );

  /**
   * Which rail tab owns each field: the root child whose subtree holds the
   * field's `fields` leaf. Empty unless the root is a `'tab'` group.
   */
  const railTabOfField = new Map<string, string>();
  for (const tab of railTabs) {
    for (const leaf of fieldLeavesOf(tab)) {
      for (const item of leaf.items) railTabOfField.set(String(item.field), tab.id);
    }
  }

  /**
   * The field focus is currently headed at, if any. The controller answers
   * per key rather than exposing the target, and at most one key answers.
   */
  const focusedField =
    controller.fields
      .map((field) => String(field.key))
      .find((key) => controller.focusTokenFor(key) !== null) ?? null;
  const focusToken = focusedField === null ? null : controller.focusTokenFor(focusedField);

  /**
   * Reveal the tab a FAILING field sits behind (#1918). A save that fails on
   * a field whose group sits behind an INACTIVE trigger would otherwise
   * report an error the operator can neither see nor fix — the panel is not
   * mounted. This is the condition #1804 batch D named when it kept `fields`
   * groups off the rail, so it has to be answered before the default may
   * flip.
   *
   * The unresolved-issue test is what makes this a REVEAL rather than a yank.
   * `useFormView` requests focus from FOUR places, and only two of them are
   * failures:
   *   1. `enterEdit(key)` — the value click / pencil on one field;
   *   2. `enterEdit()` — the header Edit button, which targets the first
   *      EDITABLE field in model order, a field unrelated to the tab being
   *      read. Switching on it would throw a reader off the tab they opened
   *      merely for pressing Edit;
   *   3. a successful save, which returns focus to the last touched field;
   *   4. a failed save — client validation or a backend 4xx — which targets
   *      the first OFFENDING field.
   * Only (4) leaves an issue on the focused field, so only (4) switches.
   *
   * The issues are readable in the very render this effect runs in: both
   * failure paths call `setIssues` BEFORE `requestFocus`, so the two state
   * updates land in one batch.
   *
   * The dependency is the focus TOKEN alone, on purpose. It changes on every
   * request, so two consecutive saves failing on the same field both switch
   * back; and a re-render that merely rebuilds the map above does not yank
   * an operator who has since clicked another trigger.
   */
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (focusedField === null) return;
    if (controller.issuesFor(focusedField).length === 0) return;
    const owner = railTabOfField.get(focusedField);
    if (owner != null) setActiveTab(owner);
  }, [focusToken]);

  /**
   * Does this tab hold a field the operator still has to fix? Only a
   * `fields` leaf can — issues are keyed by field path, and no other kind
   * owns fields — so a group tab answers for every `fields` leaf below it.
   */
  const tabHasIssues = (tab: FormNode<T, F>): boolean =>
    fieldLeavesOf(tab).some((leaf) =>
      leaf.items.some((item) => controller.issuesFor(String(item.field)).length > 0),
    );

  const formColumns = columns.filter((col) => !col.meta?.hidden);

  /**
   * The rendered columns a `fields` leaf names, in declared order. Shared by
   * every surface a leaf can land on — a rail panel, a card, an inline
   * section — so the "names a field this form does not render" check cannot
   * drift between them. Every tab's element is BUILT on every render (see
   * `tabPanels`), so it fires for every leaf rather than only the selected
   * one.
   */
  const colsOfGroup = (group: Extract<FormLeaf<T, F>, { kind: 'fields' }>): ColumnDef<T>[] =>
    group.items
      .map((item) => {
        const key = String(item.field);
        const col = formColumns.find((candidate) => columnKey(candidate) === key);
        if (!col)
          failLoud(
            `FormView group "${group.id}" names field "${key}", which this form does not render.`,
          );
        return col;
      })
      .filter((col): col is ColumnDef<T> => !!col);

  /**
   * One node's content. A leaf renders itself; a nested group frames its
   * children by its own `groupDisplay` inside a spacing container of its own.
   *
   * A `custom` leaf is rendered as an ELEMENT, never called as a function:
   * it may fetch its own data, so it must mount only when its surface is
   * shown (#2783). The change-history slot is the one exception, and its
   * contract (`FormViewSlots.changeHistory`) is to call no hooks.
   */
  const renderNode = (node: FormNode<T, F>): ReactNode => {
    if (node.kind === 'custom') {
      const Content = node.render;
      return <Content record={record as T} />;
    }
    if (node.kind === 'audit') {
      // Unreachable with a null target: the gate above removed every
      // audit node in that case. Checked rather than asserted so the
      // narrowing is a type fact.
      const auditFields = columns.flatMap((column) =>
        column.meta?.field ? [column.meta.field] : [],
      );
      return auditTarget && slots.changeHistory.render({ ...auditTarget, fields: auditFields });
    }
    if (node.kind === 'fields') {
      // The same inner containers on every surface. On the rail there is no
      // header: the trigger already carries the title, so a header here
      // would state it twice.
      //
      // `collapsible` / `defaultCollapsed` are IGNORED on the rail, and
      // deliberately so (#1918). They are `FormGroup`'s
      // progressive-disclosure mechanism; on the rail the trigger already
      // IS that mechanism, so a panel folded shut under its own active
      // trigger would be a click that reveals nothing. Rather than widen
      // them into a second, conflicting disclosure layer, they stay
      // meaningful exactly where they carry weight — see the matching note
      // on `FormNodeBase` in `@resource-kit/model`.
      return (
        <div className={embedded ? FIELD_STACK_CLASS : FIELD_GRID_CLASS}>
          {colsOfGroup(node).map(renderSlot)}
        </div>
      );
    }
    return <div className="space-y-6">{renderFramed(node.groupDisplay, node.children)}</div>;
  };

  /**
   * A `'card'` / `'inline'` group's children, each in its own `FormGroup`.
   * Never `'tab'`: only the root may be one, and the authoring checks above
   * reject a nested one. A Fragment, so the root's children land directly in
   * the outer `space-y-6` container.
   */
  const renderFramed = (display: GroupDisplay, children: readonly FormNode<T, F>[]): ReactNode => (
    <>
      {children.map((child) => (
        <FormGroup
          key={child.id}
          title={
            child.label === undefined ? undefined : t(child.label, { defaultValue: child.label })
          }
          chrome={display === 'card' && !embedded ? 'card' : 'heading'}
          collapsible={child.collapsible}
          defaultCollapsed={child.defaultCollapsed}
        >
          {renderNode(child)}
        </FormGroup>
      ))}
    </>
  );

  /**
   * A remembered tab the gate has since removed would name no trigger and no
   * panel, so selection falls back to the first tab still on the rail.
   */
  const selectedTab = hasRail
    ? (railTabs.find((tab) => tab.id === activeTab)?.id ?? railTabs[0].id)
    : null;

  /**
   * The trigger strip. Split out from the panels (#B) because the two land on
   * DIFFERENT surfaces on a full page: the strip rides the pinned masthead,
   * the panels stay in the flow below it. They remain inside one `Tabs` root
   * either way — that root is what pairs a trigger with its panel for
   * assistive tech, so splitting the MARKUP must never split the root.
   *
   * The strip scrolls sideways rather than wrapping — but that is `TabsList`'s
   * own guard now (#1961), not something this call site opts into. What stays
   * here is the part that IS specific to a pinned bar: hiding the scrollbar,
   * because the bar is chrome and its overflow is discovered by dragging, as
   * on every native tab strip.
   */
  const tabStrip = hasRail ? (
    <TabsList className="[scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {railTabs.map((tab) => (
        <TabsTrigger key={tab.id} value={tab.id}>
          {t(tab.label ?? tab.id, { defaultValue: tab.label ?? tab.id })}
          {tabHasIssues(tab) && (
            <>
              <span
                aria-hidden="true"
                className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-destructive align-middle"
              />
              {/* Colour alone is not a signal. The sr-only
                                twin joins the trigger's accessible name,
                                so the reason this tab is worth visiting
                                is announced rather than only painted. */}
              <span className="sr-only"> {t('rk.resource.tabHasErrors')}</span>
            </>
          )}
        </TabsTrigger>
      ))}
    </TabsList>
  ) : null;

  /**
   * On a full page each panel gets the card the `'card'` display gives its
   * children, so the tab content reads as one surface against the page ground
   * instead of floating on it. The drawer keeps the flat panel: a Sheet is
   * already a raised surface, and a card inside it is a second frame around
   * content that has nothing to be framed against.
   *
   * Every panel's element is BUILT here on every render, but only the
   * selected one MOUNTS: `TabsContent` does not keep an unselected panel
   * (#2783). Do not pass `keepMounted` or pre-mount hidden panels — a
   * self-fetching leaf would request its data for a tab nobody opened.
   */
  const tabPanels = hasRail
    ? railTabs.map((tab) => (
        <TabsContent key={tab.id} value={tab.id} className={embedded ? 'pt-4' : undefined}>
          {embedded ? renderNode(tab) : <Card className="p-4 sm:p-6">{renderNode(tab)}</Card>}
        </TabsContent>
      ))
    : null;

  /**
   * The leftovers: every column no `fields` leaf names. Computed ONCE, drawn
   * by ONE rule that does not read the root's `groupDisplay` — an ungrouped
   * column never had a node of its own, and minting one would need an id in
   * the very namespace the duplicate-id check above polices.
   *
   *   - No `fields` leaf at all: nothing is grouped, so the leftovers are the
   *     whole record. They render as the ungrouped "Details" fallback at the
   *     head of the body (`leading`), before a `'card'` / `'inline'` root's
   *     framed children. Under a `'tab'` root the shell places the body:
   *     after the whole rail in the embedded shell with a header, before the
   *     rail's panels everywhere else. When other content is visible and
   *     there is no column, the fallback is omitted rather than drawn as an
   *     empty shell.
   *   - Otherwise: they render as the "Other" card and come LAST, after every
   *     child (`trailing`). Under a `'tab'` root that is below the rail;
   *     drawn in the body it would LEAD the page, a card titled "Other"
   *     above the rail carrying everything else.
   *
   * The "Other" card is a BACKSTOP, not a layout every resource may lean on.
   * Under a `'tab'` root it sits outside `TabsContent`, so it shows under
   * whichever tab happens to be open — the operator reads one 「其他」
   * heading on every tab, holding whatever its author decided belonged
   * nowhere. Six resources shipped exactly that until #1969.
   *
   * So no SHIPPED resource may reach it any more:
   * `__tests__/architecture/form-tab-grouping.test.ts` R5 requires every
   * resource that declares a `fields` leaf to group every column it renders,
   * and gives the genuinely-out-of-place field a declaration instead —
   * `detailInvisible` on its list item, which drops the column before it
   * reaches `formColumns`. The card survives for hand-written fixtures:
   * rendering an orphan under a plain heading beats dropping it silently.
   */
  const renderDetails = (): ReactNode => {
    if (visibleChildren.length > 0 && formColumns.length === 0) return null;
    return embedded ? (
      <section>
        <h3 className="text-sm font-semibold text-foreground mb-1">{t('rk.resource.details')}</h3>
        <div className={FIELD_STACK_CLASS}>{formColumns.map(renderSlot)}</div>
      </section>
    ) : (
      <Card>
        <CardHeader>
          <CardTitle>{t('rk.resource.details')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className={FIELD_GRID_CLASS}>{formColumns.map(renderSlot)}</div>
        </CardContent>
      </Card>
    );
  };
  const renderOther = (): ReactNode => {
    const grouped = new Set(
      fieldLeaves.flatMap((leaf) => leaf.items.map((item) => String(item.field))),
    );
    const extras = formColumns.filter((col) => {
      const key = columnKey(col);
      return key !== '' && !grouped.has(key);
    });
    return extras.length > 0 ? (
      <FormGroup title={t('rk.resource.other')} chrome={embedded ? 'heading' : 'card'}>
        <div className={embedded ? FIELD_STACK_CLASS : FIELD_GRID_CLASS}>
          {extras.map(renderSlot)}
        </div>
      </FormGroup>
    ) : null;
  };

  if (loading) return <LoadingPage message={t('rk.resource.loadingRecord')} />;

  if (error) {
    return (
      <Card className="p-6">
        <p className="text-sm text-destructive">
          {t('rk.ui.error')}: {error}
        </p>
      </Card>
    );
  }

  if (!isCreate && !record) {
    return (
      <Card className="p-6">
        <p className="text-sm text-muted-foreground">{t('rk.resource.recordNotFound')}</p>
      </Card>
    );
  }

  // Entry point 3: the top Edit button. Same handler as the value click and
  // the pencil — the whole record enters edit.
  const editButton =
    canEdit && !editing && !isCreate ? (
      <Button variant="outline" size="sm" onClick={() => controller.enterEdit()}>
        <Pencil className="h-3.5 w-3.5 mr-1.5" />
        {t('rk.ui.edit')}
      </Button>
    ) : null;

  /**
   * `shrink-0` is deliberately ABSENT. It pinned this region at its
   * max-content width, so a resource with a long action label (orders'
   * "Refetch Shopify data") pushed itself past the right edge on any narrow
   * viewport instead of wrapping — visible in the shipped order page. On the
   * pinned bar that overflow would be permanent rather than scrolled past.
   * Right-alignment likewise starts at `sm`: once the row has wrapped onto
   * its own line, ragged-right actions under a left-aligned title read as a
   * third column that isn't there.
   */
  const headerActions =
    editButton || actions ? (
      <div
        data-testid="detail-header-actions"
        className="flex flex-wrap items-center gap-2 sm:justify-end"
      >
        {editButton}
        {actions}
      </div>
    ) : null;

  // A drawer is not a page, but a saved record still opens with the same
  // identity component as its full-page surface. Guard on the record because
  // `intent="create"` also takes the embedded shell branch without one.
  const embeddedHeader =
    embedded && showEmbeddedHeader && record != null ? (
      <PageHeader
        title={title}
        description={subtitle}
        titleAdornment={titleAdornment}
        actions={headerActions}
      />
    ) : null;

  // A `'tab'` root's children are on the rail, so its body holds only the
  // leading leftovers; a `'card'` / `'inline'` root draws its children here.
  // Create drops the tree and the leftovers alike: the flat field grid stays.
  const [leading, trailing]: [ReactNode, ReactNode] = isCreate
    ? [null, null]
    : fieldLeaves.length === 0
      ? [renderDetails(), null]
      : [null, renderOther()];
  const framed =
    form.groupDisplay === 'tab' ? null : renderFramed(form.groupDisplay, visibleChildren);
  const body = isCreate ? (
    renderCreateFields()
  ) : (
    <>
      {leading}
      {framed}
    </>
  );

  /**
   * A saved drawer record opens with its PageHeader and rail together. It has
   * no PageShell or masthead to pin: the Sheet supplies its own bounded
   * scroll. `intent="create"` lands here too; it has neither a rail nor a
   * record heading.
   */
  const embeddedRail =
    selectedTab != null ? (
      <Tabs value={selectedTab} onValueChange={(next) => setActiveTab(String(next))}>
        {tabStrip}
        {tabPanels}
      </Tabs>
    ) : null;
  const embeddedOpening =
    embeddedHeader != null ? (
      <div className="space-y-4">
        {embeddedHeader}
        {embeddedRail}
      </div>
    ) : null;
  const shell =
    embedded || isCreate ? (
      <>
        {embeddedOpening != null ? (
          <>
            {embeddedOpening}
            {body}
          </>
        ) : (
          <>
            {embedded && headerActions}
            {body}
            {embeddedRail}
          </>
        )}
        {trailing}
      </>
    ) : selectedTab != null ? (
      <Tabs value={selectedTab} onValueChange={(next) => setActiveTab(String(next))}>
        <PageShell
          title={title}
          subtitle={subtitle}
          titleAdornment={titleAdornment}
          actions={headerActions}
          tabs={tabStrip}
        >
          {body}
          {tabPanels}
          {trailing}
          {editing && <SaveBar controller={controller} isCreate={isCreate} />}
        </PageShell>
      </Tabs>
    ) : (
      <PageShell
        title={title}
        subtitle={subtitle}
        titleAdornment={titleAdornment}
        actions={headerActions}
      >
        {body}
        {tabPanels}
        {trailing}
        {editing && <SaveBar controller={controller} isCreate={isCreate} />}
      </PageShell>
    );

  return (
    <div className={cn('space-y-6', embedded ? 'p-0' : '')} onKeyDown={handleKeyDown}>
      {shell}
      {(embedded || isCreate) && editing && <SaveBar controller={controller} isCreate={isCreate} />}
    </div>
  );
}
